import { type QueryKey, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface CursorPageInfo {
  hasNextPage: boolean
  endCursor: string | null
}

interface CursorPageBase {
  items: readonly unknown[]
  pageInfo: CursorPageInfo
  /** True when the upstream could not read every row of the page. */
  partial?: boolean
}

type PageItem<TPage extends CursorPageBase> = TPage['items'][number]

export interface UseCursorPaginationParams<TPage extends CursorPageBase> {
  queryKey: QueryKey
  queryFn: (cursor: string | undefined) => Promise<TPage>
  enabled?: boolean
  staleTime?: number
  /**
   * Reset the current page back to 0 whenever this key changes. Compose it
   * from the filter values that scope the query (chainId, type filter, viewer
   * address, …) so changing a filter rewinds to the first page instead of
   * leaving the user on an orphan cursor page. A single primitive — rather
   * than an array spread into a `useEffect` deps list — keeps the deps shape
   * stable across renders and side-steps the Rules of Hooks footgun.
   */
  resetKey?: string | number
  /**
   * Keep Next available on an empty page that still points to a next one. Set it for sources
   * whose cursor is trustworthy (the oRPC procedures over the bridge, which may send an empty
   * page with a cursor). Left off, an empty page hides Next. Once no Consumer-backed caller is
   * left, drop this option together with the empty-page guard it bypasses.
   */
  trustCursorOnEmptyPage?: boolean
}

export interface CursorPaginationApi<TPage extends CursorPageBase> {
  /** The page on screen, undefined until it loads. */
  page: TPage | undefined
  items: readonly PageItem<TPage>[]
  /** The current page's `partial` flag. */
  partial: boolean
  pageIndex: number
  previousPage: () => void
  nextPage: () => void
  canPreviousPage: boolean
  canNextPage: boolean
  isLoading: boolean
  isFetching: boolean
  /**
   * True only while a user-initiated `nextPage()` click is fetching a page
   * that isn't cached yet — parallels `isLoading` but for the next page.
   * Stays false for hover-driven `prefetchNext` and background stale-time
   * refetches, so tables render skeletons on a real Next-click without
   * flashing them on hover.
   */
  isLoadingNextPage: boolean
  error: Error | null
  /**
   * True after a `nextPage()` click whose fetch failed, until the user changes page again or the
   * window resets. Unlike `error`, background refetches and hover prefetches never set it.
   */
  nextPageFailed: boolean
  /** Prefetch the next cursor page without advancing. Wire to button hover. */
  prefetchNext: () => void
  reset: () => void
}

/**
 * Cursor-aware pagination wrapper around `useInfiniteQuery`.
 *
 * Maintains a per-mount `pageIndex` that walks over the cursor pages already
 * cached by react-query. Going forward triggers `fetchNextPage()` the first
 * time a page is visited; going back is instant because the previous cursor
 * pages stay in the infinite cache. The whole window resets when `resetKey`
 * changes (filter, sort, viewer address, …) so users never end up stuck on a
 * stale cursor that no longer matches the filter.
 */
export function useCursorPagination<TPage extends CursorPageBase>({
  queryKey,
  queryFn,
  enabled = true,
  staleTime = 30 * 1000,
  resetKey,
  trustCursorOnEmptyPage = false
}: UseCursorPaginationParams<TPage>): CursorPaginationApi<TPage> {
  const [requestedPageIndex, setPageIndex] = useState(0)
  // Tracks a user-initiated next-page fetch (button click), distinct from
  // hover-driven prefetch and background refetch. Lets the table show
  // skeletons only on a real Next-click.
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false)
  const [nextPageFailed, setNextPageFailed] = useState(false)
  // Bumped every time `resetKey` changes; in-flight `fetchNextPage().finally`
  // callbacks compare their captured generation against this and bail out if
  // a reset happened mid-request, so they don't bump `pageIndex` past 0 on a
  // freshly-rewound query.
  const resetGenerationRef = useRef(0)

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) => queryFn(pageParam ?? undefined),
      initialPageParam: null as string | null,
      getNextPageParam: lastPage =>
        lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
      enabled,
      // No `placeholderData: keepPreviousData`. `useInfiniteQuery` already
      // accumulates pages in `data.pages` via `fetchNextPage` without
      // changing the queryKey, so cursor pagination never needed cross-key
      // carryover. Crucially, every other reason the key changes (filter,
      // viewer-mode marker, wallet address) is one where the previous rows
      // are stale relative to the new scope — keeping them visible across
      // an account switch (A → B) would leak A's transactions into B's
      // table until B's response resolves.
      staleTime
    })

  const fetchedPagesCount = data?.pages.length ?? 0
  // A refetch or a server reseed can drop the later pages; move back to the last page still cached
  // and store it, so a later prefetch cannot bring the stale index back.
  const pageIndex =
    fetchedPagesCount > 0 ? Math.min(requestedPageIndex, fetchedPagesCount - 1) : requestedPageIndex
  if (pageIndex !== requestedPageIndex) setPageIndex(pageIndex)
  const currentPage = data?.pages[pageIndex]

  // Filter / sort / viewer change → rewind to page 0. The react-query cache
  // is keyed off `queryKey` so a true filter change also evicts the previous
  // pages; the rewind keeps the UI consistent on partial key changes too.
  useEffect(() => {
    resetGenerationRef.current += 1
    setPageIndex(0)
    setIsLoadingNextPage(false)
    setNextPageFailed(false)
  }, [resetKey])

  const previousPage = useCallback(() => {
    setNextPageFailed(false)
    setPageIndex(Math.max(0, pageIndex - 1))
  }, [pageIndex])

  const nextPage = useCallback(() => {
    const nextIndex = pageIndex + 1
    if (nextIndex < fetchedPagesCount) {
      setNextPageFailed(false)
      setPageIndex(nextIndex)
      return
    }
    if (!hasNextPage) return
    // No `!isFetchingNextPage` guard: a hover-driven `prefetchNext` puts a
    // fetch in flight, and react-query dedupes concurrent `fetchNextPage`
    // calls into the same promise. Without this `.then` attaching to the
    // in-flight fetch, a click landing during the hover prefetch would be
    // silently dropped — `isLoadingNextPage` would stay false and the page
    // would never advance. Attaching here also flips the skeleton on for
    // the click even though the network request was already started by hover.
    const generation = resetGenerationRef.current
    setIsLoadingNextPage(true)
    // TanStack Query v5's `fetchNextPage()` always resolves (never rejects);
    // on error the resolved result has `isError: true` and `data.pages` is
    // NOT extended. Advance `pageIndex` only on success — otherwise the
    // table would land on a phantom page with no items while `error` is set
    // on the query result, instead of staying on the last valid page.
    void fetchNextPage().then(result => {
      if (resetGenerationRef.current === generation) {
        setNextPageFailed(result.isError)
        if (!result.isError) setPageIndex(nextIndex)
      }
      setIsLoadingNextPage(false)
    })
  }, [pageIndex, fetchedPagesCount, hasNextPage, fetchNextPage])

  const prefetchNext = useCallback(() => {
    const nextIndex = pageIndex + 1
    if (nextIndex < fetchedPagesCount) return
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [pageIndex, fetchedPagesCount, hasNextPage, isFetchingNextPage, fetchNextPage])

  const reset = useCallback(() => {
    setNextPageFailed(false)
    setPageIndex(0)
  }, [])

  const items = useMemo<readonly PageItem<TPage>[]>(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    () => (currentPage?.items ?? []) as readonly PageItem<TPage>[],
    [currentPage]
  )

  const canPreviousPage = pageIndex > 0
  // Unless `trustCursorOnEmptyPage` is set, gate `canNextPage` on the current
  // page actually having items: the BFF sometimes returns `items: []` with
  // `pageInfo.hasNextPage: true` on the initial cursor for a market with no
  // activity. Without this guard the table renders its empty state with a
  // still-clickable Next button under it, which `CursorPagination` would
  // otherwise self-hide once both `canPreviousPage` and `canNextPage` resolve
  // to false.
  const canNextPage =
    (trustCursorOnEmptyPage || items.length > 0) &&
    (pageIndex + 1 < fetchedPagesCount || Boolean(hasNextPage))

  return {
    page: currentPage,
    items,
    partial: currentPage?.partial ?? false,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    isLoading,
    isFetching: isFetching || isFetchingNextPage,
    isLoadingNextPage,
    error: error ?? null,
    nextPageFailed,
    prefetchNext,
    reset
  }
}
